import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { Lock, Crown, Check, Wand2 } from "lucide-react";
import { STATES, STATE_RESETS } from "../data/stateBasedResets";
import { useAuth } from "../lib/auth-context";
import { toast } from "sonner";
import { api } from "../lib/api";

const Library = () => {
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const [activeFilter, setActiveFilter] = useState("all");
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const a = await api.myAnalytics();
        setStreak(a.data.current_streak || 0);
      } catch { /* silent */ }
    })();
  }, []);

  const handleFilterClick = (id) => {
    if (!isPro) {
      toast("Filters are a Pro feature", { description: "Activate Pro to filter by state." });
      return;
    }
    setActiveFilter(id);
  };

  const handleResetClick = (stateId) => {
    if (!isPro && stateId !== "neutral") {
      toast("Locked · Pro only", {
        description: "Free includes the universal reset. Upgrade to Pro for tailored sessions.",
      });
      return;
    }
    navigate(`/?state=${stateId}`);
  };

  const items = STATES.filter((s) => activeFilter === "all" || activeFilter === s.id || !isPro);

  return (
    <div className="app-shell" data-testid="library-page">
      <Header streak={streak} />
      <section className="px-6 pt-2 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Reset Library</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          By emotional state
        </h2>
        <p className="text-sm text-[#5C7568] mt-2">
          {isPro
            ? "All states unlocked. Tap one to start that reset."
            : "Free includes the universal (Neutral) reset. Pro tailors content to your state."}
        </p>

        <div className="flex gap-2 overflow-x-auto pt-5 pb-1 -mx-6 px-6" data-testid="filter-list">
          <button
            data-testid="filter-all"
            onClick={() => handleFilterClick("all")}
            disabled={!isPro}
            className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
              activeFilter === "all" && isPro
                ? "bg-[#234B38] text-white"
                : isPro
                ? "bg-white border border-[#E2EBE6] text-[#5C7568]"
                : "bg-[#FDFBF7] border border-[#E2EBE6] text-[#5C7568]/70 cursor-not-allowed"
            }`}
          >
            {!isPro && <Lock className="w-3 h-3" />}
            All
          </button>
          {STATES.map((s) => {
            const active = activeFilter === s.id && isPro;
            return (
              <button
                key={s.id}
                data-testid={`filter-${s.id}`}
                onClick={() => handleFilterClick(s.id)}
                disabled={!isPro}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                  active
                    ? "bg-[#234B38] text-white"
                    : isPro
                    ? "bg-white border border-[#E2EBE6] text-[#5C7568]"
                    : "bg-[#FDFBF7] border border-[#E2EBE6] text-[#5C7568]/70 cursor-not-allowed"
                }`}
              >
                {!isPro && <Lock className="w-3 h-3" />}
                {s.label}
              </button>
            );
          })}
        </div>

        {!isPro && (
          <p className="text-[11px] text-[#5C7568] mt-3 italic" data-testid="filter-locked-note">
            State filters are locked on Free.
          </p>
        )}

        <div className="mt-6 space-y-3">
          {items.map((s) => {
            const content = STATE_RESETS[s.id];
            const locked = !isPro && s.id !== "neutral";
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`reset-state-${s.id}`}
                aria-disabled={locked}
                onClick={() => handleResetClick(s.id)}
                className={`w-full text-left rounded-3xl border p-5 transition-all no-tap-highlight ${
                  locked
                    ? "bg-gradient-to-br from-[#FDFBF7] to-[#F5D0D6]/20 border-[#F5D0D6]/50 cursor-not-allowed"
                    : "bg-white border-[#E2EBE6] shadow-[0_8px_32px_rgba(35,75,56,0.05)] hover:-translate-y-0.5 hover:shadow-md"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl shrink-0">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]">{s.label}</p>
                      {s.id === "neutral" && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#F5D0D6] text-[#D6336C] rounded-full px-2 py-0.5">
                          Universal
                        </span>
                      )}
                    </div>
                    <h3 className={`text-base font-medium mt-1 ${locked ? "text-[#5C7568]" : "text-[#1A2E24]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                      {content.breathwork.name}
                    </h3>
                    <p className="text-sm text-[#5C7568] mt-1 line-clamp-2">"{content.mindset}"</p>
                  </div>
                  <div className="shrink-0">
                    {locked ? (
                      <div className="w-9 h-9 rounded-full bg-white border border-[#F5D0D6] flex items-center justify-center" data-testid={`lock-state-${s.id}`}>
                        <Lock className="w-4 h-4 text-[#234B38]" strokeWidth={1.5} />
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#234B38] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" strokeWidth={2} />
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={() => navigate("/micro-actions")}
            data-testid="open-micro-library-btn"
            className="w-full rounded-3xl bg-white border border-[#E2EBE6] p-5 text-left hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#F5D0D6]/40 flex items-center justify-center">
                <Wand2 className="w-4 h-4 text-[#234B38]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]">Micro-actions</p>
                <p className="text-sm text-[#1A2E24] mt-0.5">Browse by category</p>
              </div>
              {!isPro && <Lock className="w-4 h-4 text-[#5C7568]" />}
            </div>
          </button>

          <button
            onClick={() => navigate("/sessions")}
            data-testid="open-sessions-btn"
            className="w-full rounded-3xl bg-white border border-[#E2EBE6] p-5 text-left hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#F5D0D6]/40 flex items-center justify-center">
                <Check className="w-4 h-4 text-[#234B38]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]">Reset Sessions</p>
                <p className="text-sm text-[#1A2E24] mt-0.5">Revisit past sessions</p>
              </div>
              {!isPro && <Lock className="w-4 h-4 text-[#5C7568]" />}
            </div>
          </button>
        </div>

        {!isPro && (
          <button
            onClick={() => navigate("/profile")}
            data-testid="library-unlock-btn"
            className="mt-6 w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-4 font-medium flex items-center justify-center gap-2 transition-colors shadow-md"
          >
            <Crown className="w-4 h-4" strokeWidth={1.75} />
            Unlock the full library
          </button>
        )}
      </section>
    </div>
  );
};

export default Library;
